import { Component, Input,  ElementRef, ViewChildren, QueryList, HostListener } from "@angular/core";
import { Subscription } from "rxjs";

import { c_components } from "../../..";
import { Helper } from "../../../../../4services/2helper";
import { isString } from "lodash-es";

@Component({
    selector: "c_dtable_division_list_component",
    templateUrl: "./c_dtable_division_list_component.html",
    styleUrls: ["../../common.scss", "./c_dtable_division_list_component.scss"],
    standalone: false
})
export class c_dtable_division_list_component {
  // ------------------------------------------------------------------------
  @ViewChildren('actionMenus', {read: ElementRef}) actionMenus: QueryList<any>
  @Input() public search_text: string;

  ngOnChanges(changes: any) {
    if (changes.search_text && changes.search_text.currentValue !== changes.search_text.previousValue) {
      this.on_search_text_change();
    }
  }

  isLoading: boolean = false;
  divisions: any[] = [];
  filteredDivisions = [];

  // permission
  hasReadDivisionPermission = false
  hasUpdateDivisionPermission = false
  hasDeleteDivisionPermission = false

  constructor(private helper: Helper, private c_components: c_components) {}

  private divisions$w: Subscription;
  watch() {
    this.divisions$w = this.helper.dealer_division.my_divisions$w().subscribe((v) => this.on_divisions_change(v));
  }
  unwatch() {
    this.divisions$w?.unsubscribe();
  }

  // ------------------------------------------------------------------------

  async ngOnInit() {
    this.watch();
    this.checkPermission();
    await this.load_data();
  }
  ngOnDestroy() {
    this.unwatch();
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (this.actionMenus) {
      const actionMenus = this.actionMenus.toArray();
      actionMenus.forEach(actionMenu => {
        this.filteredDivisions.forEach(division=> {
          if (('division-'+division?.id) === (actionMenu.nativeElement.id+'')) {
            if (!actionMenu.nativeElement.contains(event.target)) {
              division['isShowMenu'] = false;
            }
          }
        })
      })
    }
  }

  // ------------------------------------------------------------------------

  checkPermission(){
    this.hasReadDivisionPermission = this.helper.permission.has("dealer_division_read")
    this.hasUpdateDivisionPermission = this.helper.permission.has("dealer_division_update")
    this.hasDeleteDivisionPermission = this.helper.permission.has("dealer_division_delete")
  }

  on_search_text_change() {
    this.update_filteredDivisions();
  }

  on_divisions_change(value: any) {
    if(!value) return
    this.divisions = value;
    this.update_filteredDivisions();
  }

  async load_data() {
    this.isLoading = true
    await this.helper.dealer_division.load_my_divisions();
    this.isLoading = false
  }

  // ------------------------------------------------------------------------

  update_filteredDivisions() {
    const search_text = isString(this.search_text) ? this.search_text.toLowerCase() : '';

    // 2글자 미만이면 검색하지 않는다.
    this.filteredDivisions = search_text.length < 2
      ? [...this.divisions]
      : this.divisions.filter((division) =>
          [division.name, division.description].join("").toLowerCase().includes(search_text)
        );

    this.applySort();
  }

  // sorting -----------------------------------------------------------------

  sort = { sortColumn: '', sortDirection: '' };

  onSorted(sort: { sortColumn: string; sortDirection: string }) {
    this.sort = sort;
    this.applySort();
  }

  private applySort() {
    const { sortColumn, sortDirection } = this.sort;
    // 정렬 미지정 시 DivisionService가 내려준 이름순(numeric)을 그대로 쓴다.
    if (!sortColumn || !sortDirection) return;

    const isAsc = sortDirection === 'asc';
    this.filteredDivisions.sort((a, b) => {
      switch (sortColumn) {
        case 'name': return this.compare(a.name?.toLowerCase() ?? '', b.name?.toLowerCase() ?? '', isAsc);
        case 'site_count': return this.compare(+a.site_count || 0, +b.site_count || 0, isAsc);
        case 'user_count': return this.compare(+a.user_count || 0, +b.user_count || 0, isAsc);
        case 'description': return this.compare(a.description?.toLowerCase() ?? '', b.description?.toLowerCase() ?? '', isAsc);
        default: return 0;
      }
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    if (a === b) return 0;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  // ------------------------------------------------------------------------

  goToDetail(divisionId){
    this.helper.router.navigate_to(`/settings/divisions/${divisionId}`);
  }

  setDivisionMorePosition(division) {
    const docElem = document.documentElement;
    const elem = document.getElementById('division-' + division?.id);
    const more = document.getElementById('division-more-' + division?.id);
    if (!elem || !more) return;
    const rect = elem.getBoundingClientRect();
    const posX = docElem.clientWidth - rect.right - 10;
    const posY = rect.bottom + 4;
    more.style.right = posX + 'px';
    more.style.top = posY + 'px';
  }

  toggleMenu(division){
    const targetMenu = division.isShowMenu;
    division.isShowMenu = !targetMenu;
  }

  // ------------------------------------------------------------------------
  editDivision(division){
    this.toggleMenu(division)
    this.c_components.dialog.open('dealer_division', division.id)
  }

  deleteDivision(division){
    this.toggleMenu(division)
    this.c_components.dialog.open("warning", {
      header: `Do you want to delete Branch(${division.name})?`,
      contents: `
        <p>There are ${division.site_count} sites and ${division.user_count} user involved.</b></p>
      `,
      submit_btn: "Delete",
      submit_class: ["button-primary"],
      icon: 'warning',
      isConfirm: false,
      color: 'orange',
      submit_func: () => {
        this.delete_division(division)
      },
    });
  }

  async delete_division(division) {
    const title = `Delete a Branch`;
    const msg = `${title} ${division.name} - Success.`;
    this.isLoading = true
    try {
      await this.deleteRelatedUsers(division.id)
      await this.helper.dealer_division.delete_division(division.id);
      this.isLoading = false
      this.openConfirmDialog(title, msg);
      this.helper.dealer_division.load_my_divisions();
    } catch (err) {
      this.isLoading = false
      this.openErrorDialog(err, title);
    }
  }

  async deleteRelatedUsers(divisionId){
    await this.helper.dealer_division.load_division_members(divisionId)
    let members
    const members$w = this.helper.dealer_division.divisions_members$w().subscribe(v => members = v)
    const divisionExclusiveUsers = members.filter(member => member?.divisions?.length === 1) ?? []
    const convertAllUser = divisionExclusiveUsers.map(async v => await this.convertToGlobalUser(v?.user_id))
    await Promise.all(convertAllUser);

    members$w.unsubscribe()
  }

  async convertToGlobalUser(userId){
    if(!userId) return
    const data = { is_division_user : 0 }
    await this.helper.dealer_member.update_member(userId, data)
  }

  // ------------------------------------------------------------------------

  openConfirmDialog(header = "", msg = "", icon = "done", color = "green", doAction = false) {
    this.c_components.dialog.open("warning", {
      header: header,
      contents: `
        <p>${msg}</b></p>
      `,
      submit_btn: "OK",
      submit_class: ["button-primary"],
      icon: icon,
      isConfirm: true,
      color: color,
      submit_func: () => {
        if (doAction) {
          this.load_data();
        }
      },
    });
  }

  openErrorDialog(err, title) {
    let msg = "failed.";
    if (err.error) {
      if (err.error.message) {
        msg = err.error.message;
      }
    }
    if (err._body) {
      msg = JSON.parse(err._body).message;
    }
    setTimeout(() => {
      this.openConfirmDialog(title, msg, "warning", "orange");
    }, 200);
  }
}
